import type { PricingCampaignModel } from '@/shared/generated'

import { Slot } from '@radix-ui/react-slot'
import { propEq } from 'lodash/fp'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useWithAffectedOGsHandler } from '@/entities/optimization-group'
import {
	useDublicatePricingCampaignsMutation,
	useGlobalPcSetQuery,
} from '@/entities/pricing-campaign'

import { queryClient } from '@/shared/lib/query-client'

type Props = {
	pricingCampaignId: PricingCampaignModel['id']
	globalPcSetId: NonNullable<PricingCampaignModel['global_pc_set_id']>
	children: React.ReactNode
}
/**
 * Duplicate Pricing Campaign in place (current global set)
 */
export const DuplicatePricingCampaign = ({
	pricingCampaignId,
	globalPcSetId,
	children,
}: Props) => {
	const { t } = useTranslation()

	const { data: isMainGlobalPcSet } = useGlobalPcSetQuery(globalPcSetId, {
		select: propEq('is_main', true),
	})
	const baseDuplicatePricingCampaignToGlobalPcSetMutation =
		useDublicatePricingCampaignsMutation({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: ['pricing-campaigns'],
				})
				queryClient.invalidateQueries({
					queryKey: ['pricing-campaigns-overview'],
				})
			},
		})
	const duplicateWith424Handler = useWithAffectedOGsHandler(
		baseDuplicatePricingCampaignToGlobalPcSetMutation
	)
	const duplicatePricingCampaignToGlobalPcSetMutation = isMainGlobalPcSet
		? duplicateWith424Handler
		: baseDuplicatePricingCampaignToGlobalPcSetMutation
	const handleDublicateToGlobalPcSet = async () => {
		toast.promise(
			duplicatePricingCampaignToGlobalPcSetMutation.mutateAsync({
				src_pricing_campaign_ids: [pricingCampaignId],
				dst_entity_ids: [globalPcSetId],
				dst_entity_type: 'global_pc_set',
			}),
			{
				loading: t('general_dublicating', { defaultValue: 'Duplicating...' }),
				success: t('general_dublicated', {
					defaultValue: 'Duplicated successfully',
				}),
				error: t('general_dublication_error', {
					defaultValue: 'Error while duplicating',
				}),
			}
		)
	}

	return <Slot onClick={handleDublicateToGlobalPcSet}>{children}</Slot>
}
